<template>
  <el-dialog
      v-model="dialogVisible"
      :title="t('edit')"
      width="500"
      :before-close="handleClose"
  >
    <div>
      <el-input v-if="type != 'date'" v-model="val"></el-input>
      <el-date-picker v-else v-model="val" format="DD-MM-YYYY" type="date" style="width: 100%;" />
    </div>
    <template #footer>
      <div class="dialog-footer">
        <el-button @click="dialogVisible = false">{{t('cancel')}}</el-button>
        <el-button type="primary" @click="submit">
          {{t('confirm')}}
        </el-button>
      </div>
    </template>
  </el-dialog>
  <div class="flex gap-2 items-center">
    <div v-if="type != 'date'">{{ updatedValue }}</div>
    <div v-else>{{ formatPostgresDate(updatedValue, "DD-MM-YYYY") }}</div>
    <div @click="() => {dialogVisible = true}"
         class="cursor-pointer text-gray-400 hover:text-gray-800">
      <font-awesome-icon icon="edit"/>
    </div>
  </div>
</template>

<script setup lang="ts">
import {onMounted, ref} from 'vue'
import {useHTTP} from "@/plugins/http";
import {ElMessage, ElLoading, ElMessageBox} from "element-plus";
import {useI18n} from "vue-i18n";
import Item from '@/models/inventory/Item';
import { formatDate, formatPostgresDate } from '@/utils/date';

const props = defineProps(['stock', 'value', 'type', 'property'])
const emit = defineEmits(['merged'])

const http = useHTTP();

const messages = {
  en: {
    edit: 'Edit Value',
    pleaseWait: 'Please wait...',
    confirm: 'Confirm',
    cancel: 'Cancel',
    mergeBatchConfirmTitle: 'Merge Batches',
    mergeBatchConfirmMessage: 'This batch number already exists with the same expiry date. Do you want to merge the quantities of both batches?',
  },
  ar: {
    pleaseWait: 'يرجى الانتظار...',
    edit: 'تعديل القيمة',
    confirm: 'تعديل',
    cancel: 'الغاء',
    mergeBatchConfirmTitle: 'دمج الدفعات',
    mergeBatchConfirmMessage: 'رقم الدفعة موجود مسبقاً بنفس تاريخ الانتهاء. هل تريد دمج كميات الدفعتين؟',
  }
}
const { t } = useI18n({ messages, useScope: 'global' })

const dialogVisible = ref(false)
const isLoading = ref(false)
const val = ref("")
const updatedValue = ref("")


onMounted(() => {
  val.value = props.value ? props.value: "";
  updatedValue.value = props.value ? props.value: "";
})

const handleClose = (done: () => void) => {
  if (!isLoading.value) {
    done();
  }
}

async function submitRequest(confirmMerge = false) {
  const data: any = {}
  data.value = val.value
  data.stockId = props.stock.id
  data.property = props.property
  if (confirmMerge) {
    data.confirmMerge = true
  }
  return await http.put<any>(`${Item.ENDPOINT}/item-batches/${props.stock.id}`, data)
}

async function submit() {
  isLoading.value = true;
  const loading = ElLoading.service({
    lock: true,
    target: document.querySelector(".el-dialog"),
    text: t('pleaseWait'),
  })

  try {
    const resp = await submitRequest()

    if (resp?.mergeRequired) {
      // Close loading before showing confirm dialog
      loading.close()
      isLoading.value = false;
      dialogVisible.value = false

      try {
        await ElMessageBox.confirm(
          t('mergeBatchConfirmMessage'),
          t('mergeBatchConfirmTitle'),
          {
            confirmButtonText: t('confirm'),
            cancelButtonText: t('cancel'),
            type: 'warning',
          }
        )

        // User confirmed merge
        const mergeLoading = ElLoading.service({
          lock: true,
          text: t('pleaseWait'),
        })
        try {
          await submitRequest(true)
          mergeLoading.close()
          ElMessage({
            message: t('actions.saveChangesSuccess'),
            type: 'success'
          })
          emit('merged')
        } catch (mergeError) {
          mergeLoading.close()
          ElMessage({
            message: mergeError.body,
            type: 'error'
          })
        }
      } catch {
        // User cancelled merge — do nothing
      }
      return
    }

    updatedValue.value = val.value;
    dialogVisible.value = false
    ElMessage({
      message: t('actions.saveChangesSuccess'),
      type: 'success'
    })
  } catch (error) {
    console.log(error)
      ElMessage({
        message: error.body,
        type: 'error'
      })
  }
  loading.close()
  isLoading.value = false;
}
</script>

<style scoped>

</style>
